import React, { useState, useEffect, useCallback } from 'react';
import { 
  Star, ChevronLeft, ChevronRight, Trophy, Timer, 
  TrendingUp, Users, Loader2
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = window.location.origin;

const LiveScores = () => {
  // State
  const [activeTab, setActiveTab] = useState('live');
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [showOdds, setShowOdds] = useState(true);
  const [selectedCompetition, setSelectedCompetition] = useState('nba');
  const [showAllCompetitions, setShowAllCompetitions] = useState(false);
  const [loading, setLoading] = useState(true);
  const [liveGames, setLiveGames] = useState([]);
  const [upcomingGames, setUpcomingGames] = useState([]);
  const [recentGames, setRecentGames] = useState([]);
  const [featuredMatch, setFeaturedMatch] = useState(null);
  const [topPlayers, setTopPlayers] = useState([]);

  // Competitions
  const competitions = [
    { id: 'nba', name: 'NBA', logo: '🏀', color: 'bg-orange-600' },
    { id: 'nfl', name: 'NFL', logo: '🏈', color: 'bg-green-700' },
    { id: 'mlb', name: 'MLB', logo: '⚾', color: 'bg-red-600' },
    { id: 'nhl', name: 'NHL', logo: '🏒', color: 'bg-blue-600' },
    { id: 'mls', name: 'MLS', logo: '⚽', color: 'bg-green-600' },
    { id: 'epl', name: 'EPL', logo: '⚽', color: 'bg-purple-600' },
  ];

  const rankings = [
    { id: 'nba-standings', name: 'NBA Standings', logo: '🏀', color: 'bg-orange-600' },
    { id: 'ncaa-rankings', name: 'NCAA Rankings', logo: '🎓', color: 'bg-red-600' },
  ];

  // Sample live games data (will be replaced with API data)
  const sampleLiveGames = [
    {
      id: '1',
      conference: 'NBA Eastern Conference',
      time: '20:30',
      quarter: 'Q3',
      homeTeam: { name: 'Boston Celtics', abbr: 'BO', score: 89, color: 'bg-green-600' },
      awayTeam: { name: 'Miami Heat', abbr: 'M', score: 82, color: 'bg-red-600' },
    },
    {
      id: '2',
      conference: 'NBA Eastern Conference',
      time: '20:30',
      quarter: 'Q2',
      homeTeam: { name: 'Philadelphia 76ers', abbr: 'PH', score: 54, color: 'bg-blue-600' },
      awayTeam: { name: 'Brooklyn Nets', abbr: 'BK', score: 49, color: 'bg-gray-700' },
    },
    {
      id: '3',
      conference: 'NBA Western Conference',
      time: '21:00',
      quarter: 'Q4',
      homeTeam: { name: 'Los Angeles Lakers', abbr: 'LA', score: 102, color: 'bg-purple-600' },
      awayTeam: { name: 'Golden State Warriors', abbr: 'GS', score: 98, color: 'bg-blue-500' },
    },
    {
      id: '4',
      conference: 'NBA Western Conference',
      time: '22:00',
      quarter: 'Q1',
      homeTeam: { name: 'Phoenix Suns', abbr: 'PH', score: 18, color: 'bg-orange-500' },
      awayTeam: { name: 'Denver Nuggets', abbr: 'DE', score: 22, color: 'bg-blue-700' },
    },
  ];

  const sampleTopPlayers = [
    { name: 'LeBron James', team: 'Lakers vs Warriors', rating: 9.8, avatar: '👤' },
    { name: 'Stephen Curry', team: 'Lakers vs Warriors', rating: 9.5, avatar: '👤' },
    { name: 'Giannis Antetokounmpo', team: 'Bucks vs Knicks', rating: 9.3, avatar: '👤' },
    { name: 'Jayson Tatum', team: 'Celtics vs Heat', rating: 9.2, avatar: '👤' },
  ];

  // Fetch games data
  const fetchGames = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch upcoming games
      const upcomingRes = await fetch(`${API_URL}/api/sports/league/${selectedCompetition}/next-games?limit=10`);
      if (upcomingRes.ok) {
        const data = await upcomingRes.json();
        setUpcomingGames(data.upcoming_games || []);
      }

      // Fetch recent games
      const recentRes = await fetch(`${API_URL}/api/sports/league/${selectedCompetition}/recent-games?limit=10`);
      if (recentRes.ok) {
        const data = await recentRes.json();
        setRecentGames(data.recent_games || []);
      }

      // Use sample data for live games (API doesn't provide real-time live scores for free tier)
      setLiveGames(sampleLiveGames);
      setTopPlayers(sampleTopPlayers);
      
      // Set featured match
      if (sampleLiveGames.length > 0) {
        const featured = sampleLiveGames.find(g => g.quarter === 'Q4') || sampleLiveGames[0];
        setFeaturedMatch(featured);
      }
      
    } catch (error) {
      console.error('Error fetching games:', error);
      // Fall back to sample data
      setLiveGames(sampleLiveGames);
      setTopPlayers(sampleTopPlayers);
    } finally {
      setLoading(false);
    }
  }, [selectedCompetition]);

  useEffect(() => {
    fetchGames();
  }, [fetchGames]);

  // Format date
  const formatDate = (date) => {
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    return `${day}/${month}`;
  };

  // Navigate date
  const navigateDate = (direction) => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + direction);
    setSelectedDate(newDate);
  };

  // Group games by conference
  const groupByConference = (games) => {
    return games.reduce((acc, game) => {
      const conf = game.conference || 'Other Games';
      if (!acc[conf]) acc[conf] = [];
      acc[conf].push(game);
      return acc;
    }, {});
  };

  // Get games for active tab
  const getActiveGames = () => {
    switch (activeTab) {
      case 'live':
        return liveGames;
      case 'finished':
        return recentGames.map(game => ({
          id: game.idEvent,
          conference: game.strLeague || 'League',
          time: game.strTime || '',
          quarter: 'Final',
          homeTeam: { 
            name: game.strHomeTeam || 'Home', 
            abbr: (game.strHomeTeam || 'H').substring(0, 2).toUpperCase(), 
            score: parseInt(game.intHomeScore) || 0, 
            color: 'bg-gray-600' 
          },
          awayTeam: { 
            name: game.strAwayTeam || 'Away', 
            abbr: (game.strAwayTeam || 'A').substring(0, 2).toUpperCase(), 
            score: parseInt(game.intAwayScore) || 0, 
            color: 'bg-gray-600' 
          },
        }));
      case 'upcoming':
        return upcomingGames.map(game => ({
          id: game.idEvent,
          conference: game.strLeague || 'League',
          time: game.strTime || 'TBD',
          quarter: game.dateEvent || '',
          homeTeam: { 
            name: game.strHomeTeam || 'Home', 
            abbr: (game.strHomeTeam || 'H').substring(0, 2).toUpperCase(), 
            score: '-', 
            color: 'bg-gray-600' 
          },
          awayTeam: { 
            name: game.strAwayTeam || 'Away', 
            abbr: (game.strAwayTeam || 'A').substring(0, 2).toUpperCase(), 
            score: '-', 
            color: 'bg-gray-600' 
          },
        }));
      default:
        return liveGames;
    }
  };

  const groupedGames = groupByConference(getActiveGames());

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-green-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black p-4 md:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl md:text-3xl font-bold text-white">Live Scores</h1>
          <p className="text-gray-400 mt-1">Real-time NBA game updates and scores</p>
        </div>

        <div className="flex gap-6">
          {/* Left Sidebar - Competitions */}
          <div className="w-64 flex-shrink-0 space-y-4">
            {/* Top Competitions */}
            <div className="bg-[#1a1a2e] border border-gray-800 rounded-xl p-4">
              <h3 className="text-white font-semibold mb-4">Top Competitions</h3>
              <div className="space-y-2">
                {competitions.slice(0, showAllCompetitions ? competitions.length : 4).map(comp => (
                  <button
                    key={comp.id}
                    onClick={() => setSelectedCompetition(comp.id)}
                    className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                      selectedCompetition === comp.id 
                        ? 'bg-green-600/20 border border-green-600' 
                        : 'hover:bg-gray-800'
                    }`}
                  >
                    <span className={`w-8 h-8 ${comp.color} rounded-lg flex items-center justify-center text-lg`}>
                      {comp.logo}
                    </span>
                    <span className="text-white">{comp.name}</span>
                  </button>
                ))}
              </div>
              <button 
                onClick={() => setShowAllCompetitions(!showAllCompetitions)}
                className="w-full mt-3 text-gray-400 hover:text-white text-sm flex items-center justify-center gap-1"
              >
                Show {showAllCompetitions ? 'less' : 'more'} ▼
              </button>
            </div>

            {/* Rankings */}
            <div className="bg-[#1a1a2e] border border-gray-800 rounded-xl p-4">
              <h3 className="text-white font-semibold mb-4">Rankings</h3>
              <div className="space-y-2">
                {rankings.map(rank => (
                  <button
                    key={rank.id}
                    className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-gray-800 transition-colors"
                  >
                    <span className={`w-8 h-8 ${rank.color} rounded-lg flex items-center justify-center text-lg`}>
                      {rank.logo}
                    </span>
                    <span className="text-white">{rank.name}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1 space-y-4">
            {/* Controls Row */}
            <div className="flex items-center justify-between flex-wrap gap-4">
              {/* Filter Tabs */}
              <div className="flex items-center gap-2">
                <button className="px-4 py-2 bg-gray-800 text-white rounded-lg text-sm hover:bg-gray-700">
                  All
                </button>
                <button className="px-4 py-2 bg-gray-800 text-gray-400 rounded-lg text-sm hover:bg-gray-700 flex items-center gap-1">
                  <Star size={14} /> Favourites
                </button>
              </div>

              {/* Date Selector */}
              <div className="flex items-center gap-2">
                <button 
                  onClick={() => navigateDate(-1)}
                  className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded"
                >
                  <ChevronLeft size={18} />
                </button>
                <span className="text-white font-medium min-w-[60px] text-center">
                  {formatDate(selectedDate)}
                </span>
                <button 
                  onClick={() => navigateDate(1)}
                  className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded"
                >
                  <ChevronRight size={18} />
                </button>
              </div>

              {/* Odds Toggle */}
              <div className="flex items-center gap-2">
                <span className="text-gray-400 text-sm">Odds</span>
                <button
                  onClick={() => setShowOdds(!showOdds)}
                  className={`w-12 h-6 rounded-full transition-colors ${showOdds ? 'bg-green-500' : 'bg-gray-600'}`}
                >
                  <div className={`w-5 h-5 bg-white rounded-full transition-transform ${showOdds ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
              </div>
            </div>

            {/* Tab Buttons */}
            <div className="flex gap-2">
              {[
                { id: 'live', label: `Live (${liveGames.length})`, color: 'bg-green-600' },
                { id: 'finished', label: 'Finished', color: 'bg-gray-700' },
                { id: 'upcoming', label: 'Upcoming', color: 'bg-gray-700' },
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-6 py-2 rounded-lg text-sm font-medium transition-colors ${
                    activeTab === tab.id 
                      ? `${tab.id === 'live' ? 'bg-green-600' : 'bg-gray-700'} text-white` 
                      : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Games List */}
            <div className="bg-[#1a1a2e] border border-gray-800 rounded-xl overflow-hidden">
              {Object.keys(groupedGames).length === 0 ? (
                <div className="text-center py-12 text-gray-400">
                  <Trophy size={48} className="mx-auto mb-4 opacity-50" />
                  <p>No {activeTab} games found</p>
                </div>
              ) : (
                Object.entries(groupedGames).map(([conference, games]) => (
                  <div key={conference}>
                    {/* Conference Header */}
                    <div className="flex items-center gap-2 px-4 py-3 bg-gray-900/50 border-b border-gray-800">
                      <div className="w-6 h-6 bg-red-600 rounded-full flex items-center justify-center text-xs text-white">
                        🏀
                      </div>
                      <span className="text-white font-medium">{conference}</span>
                    </div>

                    {/* Games */}
                    {games.map(game => (
                      <div key={game.id} className="px-4 py-3 border-b border-gray-800 hover:bg-gray-900/30 transition-colors">
                        <div className="flex items-center">
                          {/* Time/Quarter */}
                          <div className="w-16 text-center">
                            <p className="text-gray-400 text-sm">{game.time}</p>
                            <p className={`text-xs font-medium ${
                              game.quarter.includes('Q') ? 'text-green-400' : 'text-gray-500'
                            }`}>
                              {game.quarter}
                            </p>
                          </div>

                          {/* Teams */}
                          <div className="flex-1 space-y-2">
                            {/* Home Team */}
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <div className={`w-8 h-8 ${game.homeTeam.color} rounded-lg flex items-center justify-center text-white text-xs font-bold`}>
                                  {game.homeTeam.abbr}
                                </div>
                                <span className="text-white">{game.homeTeam.name}</span>
                              </div>
                              <span className="text-white font-bold text-lg">{game.homeTeam.score}</span>
                            </div>

                            {/* Away Team */}
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <div className={`w-8 h-8 ${game.awayTeam.color} rounded-lg flex items-center justify-center text-white text-xs font-bold`}>
                                  {game.awayTeam.abbr}
                                </div>
                                <span className="text-white">{game.awayTeam.name}</span>
                              </div>
                              <span className="text-white font-bold text-lg">{game.awayTeam.score}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Right Sidebar - Featured Match & Top Players */}
          <div className="w-72 flex-shrink-0 space-y-4">
            {/* Featured Match */}
            {featuredMatch && (
              <div className="bg-[#1a1a2e] border border-gray-800 rounded-xl p-4">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-red-500 font-semibold text-sm flex items-center gap-1">
                    <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
                    FULL LIVE
                  </span>
                  <span className="text-gray-400 text-sm">Featured match</span>
                </div>

                {/* Score */}
                <div className="flex items-center justify-center gap-4 mb-4">
                  <div className="text-center">
                    <div className={`w-12 h-12 ${featuredMatch.homeTeam.color} rounded-xl flex items-center justify-center text-white font-bold mx-auto mb-2`}>
                      {featuredMatch.homeTeam.abbr}
                    </div>
                    <span className="text-gray-400 text-sm">{featuredMatch.homeTeam.name.split(' ').pop()}</span>
                  </div>
                  <div className="text-center">
                    <p className="text-4xl font-bold text-white">
                      {featuredMatch.homeTeam.score}:{featuredMatch.awayTeam.score}
                    </p>
                    <p className="text-gray-500 text-sm">{featuredMatch.quarter}</p>
                  </div>
                  <div className="text-center">
                    <div className={`w-12 h-12 ${featuredMatch.awayTeam.color} rounded-xl flex items-center justify-center text-white font-bold mx-auto mb-2`}>
                      {featuredMatch.awayTeam.abbr}
                    </div>
                    <span className="text-gray-400 text-sm">{featuredMatch.awayTeam.name.split(' ').pop()}</span>
                  </div>
                </div>

                {/* Odds */}
                {showOdds && (
                  <>
                    <div className="grid grid-cols-3 gap-2 mb-3">
                      <div className="bg-black rounded-lg p-3 text-center">
                        <p className="text-gray-400 text-xs mb-1">1</p>
                        <p className="text-green-400 font-semibold">+3.20</p>
                      </div>
                      <div className="bg-black rounded-lg p-3 text-center">
                        <p className="text-gray-400 text-xs mb-1">X</p>
                        <p className="text-white font-semibold">+3.75</p>
                      </div>
                      <div className="bg-black rounded-lg p-3 text-center">
                        <p className="text-gray-400 text-xs mb-1">2</p>
                        <p className="text-cyan-400 font-semibold">2.10</p>
                      </div>
                    </div>
                    <p className="text-gray-500 text-xs text-center">Additional odds</p>
                  </>
                )}
              </div>
            )}

            {/* Top Players */}
            <div className="bg-[#1a1a2e] border border-gray-800 rounded-xl p-4">
              <h3 className="text-white font-semibold mb-4">Top Players</h3>
              <div className="space-y-3">
                {topPlayers.map((player, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gray-700 rounded-full flex items-center justify-center text-lg">
                      {player.avatar}
                    </div>
                    <div className="flex-1">
                      <p className="text-white font-medium">{player.name}</p>
                      <p className="text-gray-500 text-xs">{player.team}</p>
                    </div>
                    <span className="text-green-400 font-bold">{player.rating}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LiveScores;
